import React from 'react';
import { Pill } from './Pill';
import { cn } from './cn';

export interface TabItem<K extends string = string> {
  key: K;
  label: string;
  icon?: React.ReactNode;
  count?: number;
}

export interface TabsProps<K extends string = string> {
  items: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  /** Horizontal scroll instead of wrap — useful for many tabs on mobile. */
  scrollable?: boolean;
  className?: string;
  'aria-label'?: string;
}

/**
 * A row of toggle pills used as a tab group. Replaces the bespoke tab rows in
 * the admin panels (7+ same-weight tabs) and SpotList filters with one
 * keyboard-accessible, count-aware control.
 */
export function Tabs<K extends string = string>({
  items,
  value,
  onChange,
  scrollable = false,
  className,
  ...rest
}: TabsProps<K>) {
  return (
    <div
      role="tablist"
      aria-label={rest['aria-label']}
      className={cn(
        'flex items-center gap-1.5',
        scrollable ? 'overflow-x-auto no-scrollbar -mx-1 px-1' : 'flex-wrap',
        className,
      )}
    >
      {items.map((item) => (
        <Pill
          key={item.key}
          role="tab"
          aria-selected={item.key === value}
          active={item.key === value}
          icon={item.icon}
          count={item.count}
          onClick={() => onChange(item.key)}
          className={scrollable ? 'shrink-0' : undefined}
        >
          {item.label}
        </Pill>
      ))}
    </div>
  );
}
